import {
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  FormProps,
  Input,
  notification,
  Row,
  Select,
  Table,
  TableColumnsType,
  TableProps,
} from 'antd';
import Title from 'antd/es/typography/Title';
import locale from 'antd/es/date-picker/locale/th_TH';
import dayjs from 'dayjs';
import { useState } from 'react';
import { formatDate, formatNumber } from '@/utilities/dataUtils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { IServerInfoRes } from '@/interfaces/misc';
import { getServerInfo } from '@/adapters/misc';
import classNames from 'classnames';
import PaymentInvoiceModal from './components/payment-invoice-modal';
import { getQueryParam } from '@/utilities/queryUtils';
import { jobProcess } from '@/adapters/job';
import {
  InvoiceList,
  ISellerPayment,
  ISellerSearchInvoiceReq,
  ISellerSearchInvoiceRes,
  ISellerSearchPaymentReq,
  ISellerSearchPaymentRes,
} from '@/interfaces/seller';
import CustomerSelector from '@/components/selector/customer-selector';
import { ICustomer } from '@/interfaces/customer';
import {
  confirmSellerPayment,
  searchSellerInvoicePayment,
  searchSellerPayment,
} from '@/adapters/seller';
import {
  getSellerBillingAutoStatusOptions,
  getSellerPaymentStatusColor,
  getSellerPaymentStatusName,
} from '@/helpers/seller';
import { ESellerInvoiceStatus } from '@/enums/seller';

const { RangePicker } = DatePicker;

const _criteria: ISellerSearchPaymentReq = {
  dateFrom: dayjs().startOf('month').format('YYYY-MM-DD'),
  dateTo: dayjs().endOf('month').format('YYYY-MM-DD'),
  docNo: '',
  customer: null,
  status: null,
};

const _criteriaINV: ISellerSearchInvoiceReq = {
  docNo: '',
  dateFrom: null,
  dateTo: null,
  customer: undefined,
  status: ESellerInvoiceStatus.ACPT,
};

const PaymentPage: React.FC = () => {
  const job = getQueryParam('job');
  const [form] = Form.useForm();
  const [criteria, setCriteria] = useState<ISellerSearchPaymentReq>({
    ..._criteria,
  });

  const [criteriaINV, setCriteriaINV] = useState<ISellerSearchInvoiceReq>({
    ..._criteriaINV,
  });

  const [selectedRowKey, setSelectedRowKey] = useState<ISellerPayment[]>([]);

  const [invoiceModal, setInvoiceModal] = useState<{
    open: boolean;
    invoiceList: InvoiceList[];
  }>({
    open: false,
    invoiceList: [],
  });

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 6 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 14 },
    },
  };

  const onFinish: FormProps<ISellerSearchPaymentReq>['onFinish'] = (values) => {
    setCriteria({
      ...criteria,
      docNo: values.docNo,
      dateFrom: values.docDate?.[0].format('YYYY-MM-DD') || undefined,
      dateTo: values.docDate?.[1].format('YYYY-MM-DD') || undefined,
      customer: values.customer,
      status: values.status,
      // docStatus: values.docStatus,
    });
  };

  const onClear = () => {
    form.setFieldsValue({
      ..._criteria,
      docDate: [dayjs(_criteria.dateFrom), dayjs(_criteria.dateTo)],
    });
    form.submit();
  };

  const columns: TableColumnsType<ISellerPayment> = [
    {
      title: 'ลำดับ',
      dataIndex: '',
      width: 60,
      align: 'center',
      className: 'text-bold',
      render: (_, rec, i) => <div className="font-weight-bold">{i + 1}</div>,
    },
    {
      title: 'เอกสารจ่าย',
      dataIndex: 'PAYMENT_NBR',
      align: 'center',
      className: 'text-bold',
      width: 120,
      fixed: 'left',
    },
    {
      title: 'วันที่โอน',
      dataIndex: 'AR_EXPT_DATE',
      align: 'center',
      className: 'text-bold',
      width: 120,
      render: (_) => formatDate(_),
    },
    {
      title: 'ลูกหนี้',
      dataIndex: 'CUSTOMER_CODE',
      align: 'center',
      className: 'text-bold',
      width: 120,
    },
    {
      title: 'ชื่อลูกหนี้',
      dataIndex: 'CUSTOMER_NAME',
      className: 'text-bold',
    },
    {
      title: 'ยอดเงิน',
      dataIndex: 'AR_AMT',
      align: 'right',
      width: 120,
      className: 'text-bold text-primary',
      render: (_) => formatNumber(_, 2),
    },
    {
      title: 'สถานะเอกสาร',
      dataIndex: 'AP_STATUS',
      align: 'center',
      width: 200,
      render: (_) => (
        <div
          className={`${classNames(getSellerPaymentStatusColor(_))} rounded`}
        >
          {getSellerPaymentStatusName(_)}
        </div>
      ),
    },
    {
      title: 'CONFIRM',
      dataIndex: 'AR_AUTO_STATUS',
      align: 'center',
      width: 200,
      render: (_) => (
        <div className={(_ && _ === 'OK' && 'text-success') || 'text-warning'}>
          {(_ && _ === 'OK' && 'ยืนยันแล้ว') || 'รอยืนยัน'}
        </div>
      ),
    },
    {
      title: '',
      dataIndex: 'InvoiceList',
      align: 'center',
      width: 120,
      render: (_, rec) => (
        <div>
          {
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => onGetInvoiceList(rec.PAYMENT_NBR)}
            >
              รายการ
            </button>
          }
        </div>
      ),
    },
  ];

  const { data: payments, refetch } = useQuery<
    AxiosResponse<ISellerSearchPaymentRes>,
    Error
  >({
    queryKey: ['search-seller-payment-inquiry', criteria],
    queryFn: async () => await searchSellerPayment(criteria),
  });

  const { data: serverInfo } = useQuery<AxiosResponse<IServerInfoRes>, Error>({
    queryKey: ['search-server-info', criteria],
    queryFn: async () => await getServerInfo(),
  });

  const mutations = useMutation({
    mutationFn: async (data: string[]) => await confirmSellerPayment(data),
    mutationKey: ['payment-entry'],
    onSuccess: (data) => {
      if (data.status === 200) {
        notification.success({
          message: 'บันทึกรับเอกสารเรียบร้อย',
        });
        refetch();
        setSelectedRowKey([]);
      } else {
      }
    },
    onError: (error) => {
      console.log('error', error);
    },
  });

  function onEntry(): void {
    mutations.mutateAsync(
      selectedRowKey.map((c) => {
        return c.PAYMENT_NBR;
      })
    );
  }

  const rowSelection: TableProps<ISellerPayment>['rowSelection'] = {
    onChange: (
      selectedRowKeys: React.Key[],
      selectedRows: ISellerPayment[]
    ) => {
      setSelectedRowKey(selectedRows);
    },
    getCheckboxProps: (record: ISellerPayment) => ({
      disabled: ['OK'].includes(record.AR_AUTO_STATUS || ''),
    }),
    selectedRowKeys: selectedRowKey?.map((c) => c.PAYMENT_NBR),
  };

  const { data: inv } = useQuery<AxiosResponse<ISellerSearchInvoiceRes>, Error>(
    {
      queryKey: ['search-seller-inquiry', criteriaINV],
      queryFn: async () => await searchSellerInvoicePayment(criteriaINV),
      enabled: !!criteriaINV.paymentCode,
    }
  );

  const onGetInvoiceList = (paymentCode: string) => {
    setCriteriaINV({
      ..._criteriaINV,
      paymentCode: paymentCode,
    });
    setInvoiceModal({
      invoiceList: [],
      open: true,
    });
  };

  return (
    <>
      <Flex justify="space-between" gap={2}>
        <div>
          <Title level={2} className="text-center">
            {serverInfo?.data?.data?.[0]?.SERVER_NAME}
          </Title>
        </div>
        <div>
          <Title
            level={2}
            className="text-center"
            onClick={() => (job === 'x12' ? jobProcess('payment') : undefined)}
          >
            ค้นหาเอกสารรับจากธนาคาร
          </Title>
        </div>
      </Flex>
      <Card>
        <Form
          {...formItemLayout}
          layout={'horizontal'}
          form={form}
          initialValues={{
            ...criteria,
            docDate: [dayjs(criteria.dateFrom), dayjs(criteria.dateTo)],
          }}
          onFinish={onFinish}
          autoComplete="off"
          labelAlign={'left'}
        >
          <Row gutter={24}>
            <Col md={12}>
              <Form.Item name="docNo" label="เลขที่เอกสาร">
                <Input placeholder="เลขที่เอกสาร" size="large" />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name={'docDate'} label="วันที่เอกสาร">
                <RangePicker
                  size="large"
                  style={{ width: '100%' }}
                  locale={locale}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col md={12}>
              <Form.Item<ISellerSearchPaymentReq>
                name="customer"
                label="ลูกค้า"
              >
                <CustomerSelector
                  handleSelectData={function (customer: ICustomer): void {
                    form.setFieldsValue({
                      customer: customer?.CMAD_ID,
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item<ISellerSearchPaymentReq> name="status" label="สถานะ">
                <Select
                  showSearch
                  placeholder="เลือกสถานะ"
                  optionFilterProp="label"
                  size="large"
                  defaultValue={criteria?.status}
                  options={[...getSellerBillingAutoStatusOptions()]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Flex gap={24} justify="center">
            <button
              type="button"
              className="btn btn-success"
              style={{ minWidth: 150 }}
              disabled={!selectedRowKey?.length}
              onClick={() => onEntry()}
            >
              {mutations?.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Loading...</span>
                </>
              ) : (
                'บันทึกรับเอกสารรับ'
              )}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ minWidth: 150 }}
            >
              ค้นหา
            </button>
            <button
              type="button"
              className="btn btn-danger"
              style={{ minWidth: 150 }}
              onClick={onClear}
            >
              ยกเลิก
            </button>
          </Flex>
        </Form>
      </Card>
      <Card className="mt-3">
        <Table<ISellerPayment>
          rowSelection={{ type: 'checkbox', ...rowSelection }}
          columns={columns}
          dataSource={payments?.data?.data || []}
          size="small"
          bordered
          rowKey={(c) => c.PAYMENT_NBR}
          scroll={{ x: 1400 }}
        />
      </Card>
      <PaymentInvoiceModal
        open={invoiceModal?.open || false}
        invoiceList={(inv?.data?.data as any) || []}
        onClose={function (): void {
          setInvoiceModal({
            invoiceList: [],
            open: false,
          });
        }}
      />
    </>
  );
};

export default PaymentPage;
